import { MaterialModule } from 'src/app/material.module';
import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { IFormConfig } from 'src/app/interfaces/IForm';
import { AuthService } from 'src/app/services/auth.service';
import { ToastService } from 'src/app/services/toast.service';
import { FormControlType } from 'src/app/shared/enums';
import { DynamicFormComponent } from 'src/app/shared/components/dynamic-form/dynamic-form.component';
import { HttpErrorResponse } from '@angular/common/http';
import { PasswordStrengthComponent } from 'src/app/shared/components/strength-password/password-strength.component';

@Component({
    selector: 'app-change-password-modal',
    imports: [
        TranslateModule,
        DynamicFormComponent,
        RouterModule,
        MaterialModule,
        TablerIconsModule
    ],
    templateUrl: './change-password-modal.component.html',
    styleUrls: ['./change-password-modal.component.scss']
})
export class ChangePasswordModalComponent implements OnInit {
  @ViewChild(DynamicFormComponent) dynamicFormComponent: DynamicFormComponent;
  changePasswordForm: FormGroup;
  spinLoader: boolean = false;
  formConfigs: IFormConfig = {
    id: 'changePasswordModal',
    styles: { footerClass: 'justify-content-end b-t-1 p-l-24 p-r-24' },
    actions: [
      {
        id: 'cancel',
        text: 'cancel',
        isStroked: true,
        layoutClass: "green_theme",
        onClick: () => {
          this.dialog.closeAll();
        }
      },
      {
        id: 'save',
        text: 'save',
        disabled: () => this.changePasswordForm.invalid,
        layoutClass: "green_theme",
        type: 'submit'
      }
    ],
    items: [
      {
        key: 'password',
        label: 'current password',
        layoutConfig: [{ column: 12 }],
        controlType: FormControlType.INPUT,
        inputConfig: { isPassword: true },
        validator: [{ errorType: 'required', errorMessage: 'current password is required' }]
      },
      {
        key: 'newPassword',
        label: 'new password',
        layoutConfig: [{ column: 12 }],
        controlType: FormControlType.INPUT,
        inputConfig: { isPassword: true },
        validator: [{ errorType: 'required', errorMessage: 'new password is required' }]
      },
      {
        key: 'passwordStrength',
        layoutConfig: [{ column: 12 }],
        controlType: FormControlType.DYNAMIC_COMPONENT,
        dynamicComponentConfig: { component: PasswordStrengthComponent, inputs: { passwordKey: 'newPassword' } }
      },
      {
        key: 'confirmPassword',
        label: 'confirm password',
        layoutConfig: [{ column: 12 }],
        controlType: FormControlType.INPUT,
        inputConfig: { isPassword: true },
        validator: [
          { errorType: 'required', errorMessage: 'confirm password is required' },
          { errorType: 'mismatch', errorMessage: 'confirm password do not match' }
        ]
      },
    ],
  };

  constructor(
    private formBuilder: FormBuilder,
    public dialog: MatDialog,
    private toastService: ToastService,
    private translateService: TranslateService,
    private authService: AuthService) { }

  ngOnInit() {
    this.initForm();
  }

  initForm() {
    this.changePasswordForm = this.formBuilder.group({
      userId: [this.authService.loggerUser?.id],
      password: [null, Validators.required],
      newPassword: [null, Validators.required],
      confirmPassword: [null, [Validators.required, this.matchValidator.bind(this)]],
    });
    this.changePasswordForm.get('newPassword')?.valueChanges.subscribe(() => {
      this.changePasswordForm.get('confirmPassword')?.updateValueAndValidity();
    });
  }

  matchValidator(control: AbstractControl): { [key: string]: boolean } | null {
    if (!control?.parent) {
      return null;
    }
    const newPassword = control.parent.get('newPassword')?.value;
    const confirmPassword = control.value;
    return newPassword && confirmPassword && newPassword !== confirmPassword ? { mismatch: true } : null;
  }

  onSubmit = () => {
    if (this.changePasswordForm.valid) {
      this.spinLoader = true;
      this.authService.resetPassword(this.changePasswordForm.value).subscribe({
        next: () => {
          this.toastService.success(this.translateService.instant('update successfully'));
          this.closeDialog();
          this.spinLoader = false;
        },
        error: (error: HttpErrorResponse) => {
          this.spinLoader = false;
          this.dynamicFormComponent.ValidatorError = error;
        }
      });
    } else {
      this.changePasswordForm.markAllAsTouched();
    }
  }

  closeDialog() {
    this.dialog.closeAll();
  }
}
